<template>
  <component
    :is="tag"
    class="skeleton-standard"
    :class="classes"
    :style="styles"
  />
</template>

<script setup>
import { computed } from 'vue'
import { cx } from '@/helpers'

defineOptions({ name: 'Skeleton' })
const props = defineProps({
  tag: { type: String, default: 'div' },
  type: {
    type: String,
    validator: v => [
      'text',
      'rect',
      'circle',
      'button',
      'badge',
      'chip',
      'toolbar',
      'checkbox',
      'radio',
      'toggle',
      'slider',
      'range',
      'input',
      'avatar'
    ].includes(v),
    default: 'rect'
  },
  animation: {
    type: String,
    validator: v => [
      'wave',
      'pulse',
      'pulse-x',
      'pulse-y',
      'fade',
      'blink',
      'none'
    ].includes(v),
    default: 'blink'
  },
  animationSpeed: { type: [String, Number], default: 1500 },
  square: { type: Boolean, default: false },
  bordered: { type: Boolean, default: false },
  size: { type: String, default: null },
  width: { type: [Number, String], default: null },
  height: { type: [Number, String], default: null },
  padding: {
    type: Array,
    default: () => [0, 0],
    validator: v => Array.isArray(v)
      && (v.length === 2 || v.length === 4)
      && v.every(e => typeof e === 'number' || typeof e === 'string')
  }
})

const classes = computed(() => cx(
  `type-${props.type}`,
  props.square && 'is-square',
  props.bordered && 'is-bordered',
  props.animation !== 'none' && `is-animated anim-${props.animation}`
))

const styles = computed(() => {
  const width = typeof props.width === 'number'
    ? `${props.width}px`
    : props.width
  const height = typeof props.height === 'number'
    ? `${props.height}px`
    : props.height
  let size = typeof props.size === 'number'
    ? `${props.size}px`
    : props.size

  size = props.size ? [size, size] : [width, height]

  const padding = props.padding.map(p => typeof p === 'number' ? `${p}px` : p)
  const inset = padding.length === 4
    ? padding.join(' ')
    : `${padding.join(' ')} ${padding.join(' ')}`

  return {
    '--anim-speed': `${props.animationSpeed}ms`,
    '--inset': inset,
    'width': size[0],
    'height': size[1]
  }
})
</script>

<style scoped>
@reference "#app-styles.css";

.skeleton-standard {
  --anim-speed: 1500ms;
  --animate-skeleton-fade: skeleton-fade var(--anim-speed) linear 0.5s infinite;
  --animate-skeleton-pulse: skeleton-pulse var(--anim-speed) ease-in-out 0.5s infinite;
  --animate-skeleton-pulse-x: skeleton-pulse-x var(--anim-speed) ease-in-out 0.5s infinite;
  --animate-skeleton-pulse-y: skeleton-pulse-y var(--anim-speed) ease-in-out 0.5s infinite;
  --animate-skeleton-wave: skeleton-wave var(--anim-speed) linear 0.5s infinite;

  @apply rounded-sm box-border;

  &.is-animated { @apply cursor-wait; }

  &::before { @apply content-["\00a0"]; }

  &.type-circle,
  &.type-avatar { @apply h-12 min-w-12 max-w-12 rounded-full; }
  &.type-button { @apply w-24 h-9 rounded-full; }
  &.type-badge { @apply w-18 h-4 rounded-full; }
  &.type-chip { @apply w-24 h-7 rounded-sm; }
  &.type-toolbar { @apply h-12; }
  &.type-checkbox,
  &.type-radio { @apply h-10 min-w-10 max-w-10 rounded-full; }
  &.type-toggle { @apply w-14 h-10 rounded-sm; }
  &.type-slider,
  &.type-range { @apply h-10; }
  &.type-input { @apply h-14; }

  &.is-bordered { @apply border border-secondary/5; }
  &.is-square { @apply rounded-none; }

  &.anim-fade { @apply animate-skeleton-fade; }
  &.anim-pulse { @apply animate-skeleton-pulse; }
  &.anim-pulse-x { @apply animate-skeleton-pulse-x; }
  &.anim-pulse-y { @apply animate-skeleton-pulse-y; }
  &.anim-wave,
  &.anim-blink,
  &.anim-pop {
    @apply relative overflow-hidden z-1;
    &::after { @apply content-[''] absolute inset-(--inset) z-0; }
  }
  &.anim-blink::after { @apply bg-secondary-fainter/70 animate-skeleton-fade; }
  &.anim-wave:after { @apply animate-skeleton-wave bg-linear-to-r from-secondary-fainter/0 via-secondary-fainter/50 to-secondary-fainter/0; }
}
</style>
